import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { useRfqMutations } from '../hooks/useRfqMutations'
import { describeCostControlError, isRecordGoneError } from '../lib/errors'
import { todayIso } from '../lib/invoices'
import { formatMoney, moneyLocale } from '../lib/money'
import { recordDetailState } from '../lib/records'
import { awardResultPath, quotePrefill, rfqAffordances } from '../lib/rfq'
import { buildTree } from '../lib/tree'
import type { CostContract, CostControlView, CostRfqDetail, CostRfqQuote } from '../types'
import QuoteAttachmentsDialog from './QuoteAttachmentsDialog'
import QuoteDialog from './QuoteDialog'
import RfqDetailSkeleton from './RfqDetailSkeleton'
import RfqDialog from './RfqDialog'
import RfqHeader from './RfqHeader'
import RfqQuotesCard from './RfqQuotesCard'
import RfqSummaryCard from './RfqSummaryCard'

const NOT_FOUND = 'CostControl_error_rfq_not_found'
type Pending = { kind: 'cancel' | 'reopen' | 'revoke' | 'delete' } | { kind: 'award' | 'deleteQuote'; quote: CostRfqQuote } | null

/** Part D spec §8: the RFQ's brief, its quotes, award and revoke, attachments and history. */
const RfqDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { match, history } = useRouter()
  const { projectId, rfqId } = match.params as { projectId: string; rfqId: string }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const { data, loading, error } = useQuery<{ costRfq: CostRfqDetail }>(Query.COST_RFQ, {
    variables: { id: rfqId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The item tree for the edit dialog's budget item picker.
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The contracts give a change RFQ's first supplier its counterparty.
  const { data: contractData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const currency = useCostCurrency(projectId)
  // A write's reload of this RFQ can be refused (someone deleted it); the page's own query never hears of it.
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isRecordGoneError(message, NOT_FOUND)) setLost({ id, message })
  }, [])
  const mutations = useRfqMutations(projectId, onDetailError)
  const [editing, setEditing] = useState(false)
  const [quoteEditing, setQuoteEditing] = useState<CostRfqQuote | 'new' | null>(null)
  const [filesQuoteId, setFilesQuoteId] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const detail = data?.costRfq
  const message = (lost && lost.id === rfqId ? lost.message : null) ?? error?.message ?? null
  const state = recordDetailState({
    loading, hasData: !!detail, recordProjectId: detail?.rfq.projectId ?? null, projectId, message,
    rightsLoading: actions.loading, canView: actions.canView, notFoundKey: NOT_FOUND,
  })
  const af = rfqAffordances(actions, state === 'ready' && detail ? detail.rfq : null)
  // A dialog closes as soon as its action is no longer allowed: someone else decided the RFQ meanwhile.
  useEffect(() => { if (!af.edit) setEditing(false) }, [af.edit])
  useEffect(() => { if (!af.editQuotes) setQuoteEditing(null) }, [af.editQuotes])
  const back = () => history.push(`/project/${projectId}/cost-control?tab=rfqs`)

  if (state === 'loading') return <RfqDetailSkeleton />
  if (state !== 'ready' || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t('CostControl_rfq_back')}
        </Button>
        <p className="text-sm text-muted-foreground">{state === 'error' ? describeCostControlError(message, t) : t(NOT_FOUND)}</p>
      </div>
    )
  }

  const { rfq, quotes } = detail
  const contracts = contractData?.costContracts ?? []
  const money = (v: string) => formatMoney(v, { locale, currency })
  const filesQuote = quotes.find((q) => q.id === filesQuoteId) ?? null
  const confirmText = !pending ? null
    : pending.kind === 'award' ? {
      title: 'CostControl_rfq_award_title', label: 'CostControl_rfq_award',
      text: t(rfq.contractId ? 'CostControl_rfq_award_text_change' : 'CostControl_rfq_award_text_contract', { supplier: pending.quote.supplierName, amount: money(pending.quote.amount ?? '0') }),
    }
    : pending.kind === 'deleteQuote' ? { title: 'CostControl_rfq_quote_delete_title', label: 'Common_delete', text: t('CostControl_rfq_quote_delete_text', { supplier: pending.quote.supplierName }) }
    : pending.kind === 'delete' ? { title: 'CostControl_rfq_delete_title', label: 'Common_delete', text: t('CostControl_rfq_delete_text') }
    : pending.kind === 'revoke' ? { title: 'CostControl_rfq_revoke_title', label: 'CostControl_rfq_revoke', text: t('CostControl_rfq_revoke_text') }
    : pending.kind === 'cancel' ? { title: 'CostControl_rfq_cancel_title', label: 'CostControl_rfq_cancel', text: t('CostControl_rfq_cancel_text') }
    : { title: 'CostControl_rfq_reopen_title', label: 'CostControl_rfq_reopen', text: t('CostControl_rfq_reopen_text') }
  const destructive = !!pending && (pending.kind === 'delete' || pending.kind === 'deleteQuote' || pending.kind === 'revoke')

  const confirm = async () => {
    const p = pending
    if (!p) return
    if (p.kind === 'award') {
      const res = await mutations.award(rfq.id, p.quote.id)
      setPending(null)
      const path = res.ok ? awardResultPath(projectId, res.data.awardCostRfq) : null
      if (path) history.push(path)
      return
    }
    if (p.kind === 'deleteQuote') await mutations.removeQuote(rfq.id, p.quote.id)
    else if (p.kind === 'cancel') await mutations.cancel(rfq.id)
    else if (p.kind === 'reopen') await mutations.reopen(rfq.id)
    else if (p.kind === 'revoke') await mutations.revoke(rfq.id)
    else {
      const res = await mutations.remove(rfq.id)
      setPending(null)
      if (res.ok) {
        toastr.success('', t('CostControl_rfq_deleted'))
        back()
      }
      return
    }
    setPending(null)
  }
  const saveQuote = async (input: Record<string, unknown>) => {
    if (!quoteEditing) return false
    const res = quoteEditing === 'new' ? await mutations.addQuote(rfq.id, input) : await mutations.updateQuote(rfq.id, quoteEditing.id, input)
    return res.ok
  }

  return (
    <div className="space-y-4">
      <RfqHeader
        rfq={rfq}
        af={af}
        onBack={back}
        onEdit={() => setEditing(true)}
        onCancel={() => setPending({ kind: 'cancel' })}
        onReopen={() => setPending({ kind: 'reopen' })}
        onRevoke={() => setPending({ kind: 'revoke' })}
        onDelete={() => setPending({ kind: 'delete' })}
      />
      <RfqSummaryCard rfq={rfq} projectId={projectId} currency={currency} locale={locale} today={todayIso()} />
      <RfqQuotesCard
        quotes={quotes}
        af={af}
        currency={currency}
        locale={locale}
        onAdd={() => setQuoteEditing('new')}
        onEdit={setQuoteEditing}
        onDelete={(quote) => setPending({ kind: 'deleteQuote', quote })}
        onAward={(quote) => setPending({ kind: 'award', quote })}
        onFiles={(quote) => setFilesQuoteId(quote.id)}
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordAttachmentsCard
          projectId={projectId}
          attachments={detail.attachments}
          canAdd={af.addAttachment}
          canRemove={af.removeAttachment}
          canLinkDocuments={docControl.canView}
          onAdd={(source) => mutations.addAttachment(rfq.id, { rfqId: rfq.id }, source)}
          onRemove={(attachmentId) => mutations.removeAttachment(rfq.id, attachmentId)}
        />
        <RecordHistoryCard projectId={projectId} entityType="RFQ" entityId={rfq.id} currency={currency} locale={locale} />
      </div>
      <RfqDialog open={editing} mode="edit" rfq={rfq} contracts={contracts} tree={tree} onOpenChange={setEditing} onSubmit={async (input) => (await mutations.update(rfq.id, input)).ok} />
      <QuoteDialog
        open={!!quoteEditing}
        quote={quoteEditing === 'new' ? null : quoteEditing}
        prefill={quoteEditing === 'new' ? quotePrefill(rfq, quotes.length, contracts) : null}
        currency={currency}
        onOpenChange={(open) => { if (!open) setQuoteEditing(null) }}
        onSubmit={saveQuote}
      />
      <QuoteAttachmentsDialog
        quote={filesQuote}
        projectId={projectId}
        canAdd={af.addAttachment}
        canRemove={af.removeAttachment}
        canLinkDocuments={docControl.canView}
        onClose={() => setFilesQuoteId(null)}
        onAdd={(quoteId, source) => mutations.addAttachment(rfq.id, { rfqQuoteId: quoteId }, source)}
        onRemove={(attachmentId) => mutations.removeAttachment(rfq.id, attachmentId)}
      />
      <ConfirmDialog
        open={!!pending}
        title={confirmText ? t(confirmText.title) : ''}
        description={confirmText?.text}
        confirmLabel={confirmText ? t(confirmText.label) : ''}
        variant={destructive ? 'destructive' : 'default'}
        submitting={mutations.saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  )
}
export default RfqDetail
